import { useState } from 'react';
import cn from 'classnames';
import { DocumentText, Edit2, Eye, Trash } from 'iconsax-react';
import InfoText from '../atoms/InfoText';
import TitleText from '../atoms/TitleText';
import { useTranslation } from 'react-i18next';
import CustomModal from '../atoms/CustomModal';
import { Carousel, Image } from 'antd';

interface Attachment {
  url: string;
  type: string;
  id?: string;
  name?: string;
  size?: number;
}

type MediaType = 'image' | 'video' | 'document';

interface Props {
  date?: string;
  title: string;
  description?: string;
  specs?: string;
  link?: string;
  itemId?: string;
  isEditable: boolean;
  isDelete: boolean;
  noBorder?: boolean;
  onDelete?: (arg: unknown) => void;
  onEdit?: (arg: unknown) => void;
  imageUrl?: string;
  images?: string[];
  attachments?: Attachment[];
  dataTestId?: string;
  listAttachmentsMode?: boolean;
  catalogueMode?: boolean;
}

const getMediaType = (file: Attachment): MediaType => {
  const url = file.url.toLowerCase();
  const isDocument = url.endsWith('.pdf') || url.endsWith('.doc') || url.endsWith('.docx');
  const isImage =
    !isDocument &&
    (file.type?.includes('image') ||
      file.type === 'IMAGE' ||
      !!url.match(/\.(jpg|jpeg|png|gif|webp)$/));
  const isVideo =
    !isDocument &&
    (file.type?.includes('video') ||
      file.type === 'VIDEO' ||
      !!url.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/));

  if (isImage) return 'image';
  if (isVideo) return 'video';
  return 'document';
};

export default function PreviewProfileItem({
  date,
  title,
  description,
  isDelete,
  isEditable,
  itemId,
  onDelete,
  onEdit,
  link,
  noBorder,
  dataTestId,
  imageUrl,
  images = [],
  attachments = [],
  listAttachmentsMode = false,
  catalogueMode = false,
  specs,
}: Props) {
  const { t } = useTranslation();
  const [filePreview, setFilePreview] = useState<{ url: string; type: MediaType } | null>(null);

  const displayAttachments: Attachment[] = (attachments.length > 0
    ? attachments
    : images.length > 0
      ? images.map((img) => ({ url: img, type: 'IMAGE' }))
      : imageUrl
        ? [{ url: imageUrl, type: 'IMAGE' }]
        : []
  ).map((file: Attachment) => ({
    ...file,
    url: file.url || (file as any).fileUrl || (file as any).blobFileUrl || '',
  }));

  const openFileModal = (file: Attachment) => {
    const mediaType = getMediaType(file);

    if (mediaType === 'image' || mediaType === 'video') {
      setFilePreview({ url: file.url, type: mediaType });
    } else {
      window.open(file.url, '_blank');
    }
  };

  const renderMedia = (file: Attachment, className: string, isThumbnail = false) => {
    const mediaType = getMediaType(file);

    if (mediaType === 'image') {
      return (
        <img
          src={file.url}
          alt={title}
          className={cn(className, 'object-cover transition-all ease-in-out duration-300', {
            'hover:opacity-50 hover:cursor-pointer': isThumbnail,
          })}
        />
      );
    }

    if (mediaType === 'video') {
      return (
        <video
          src={file.url}
          className={cn(className, 'object-cover bg-black')}
          controls={!isThumbnail}
          preload="metadata"
          playsInline
        />
      );
    }

    return (
      <div className={cn(className, 'flex flex-col items-center justify-center bg-gray-100 dark:bg-dark-bg-secondary gap-2')}>
        <DocumentText size={isThumbnail ? 40 : 64} variant="Bold" className="text-light-button-primary dark:text-blue-400" />
        {!isThumbnail && (
          <span className="text-xs text-gray-500 text-center px-4 truncate w-full">{file.name || 'Document'}</span>
        )}
      </div>
    );
  };

  const renderCarouselSlide = (file: Attachment) => {
    const mediaType = getMediaType(file);

    if (mediaType === 'image') {
      return (
        <img
          src={file.url}
          alt={title}
          className="w-full h-full object-cover cursor-pointer"
          onClick={() => setFilePreview({ url: file.url, type: 'image' })}
        />
      );
    }

    if (mediaType === 'video') {
      return (
        <video
          src={file.url}
          className="w-full h-full object-cover bg-black"
          controls
          preload="metadata"
          playsInline
        />
      );
    }

    return (
      <div
        className="w-full h-full flex flex-col items-center justify-center bg-gray-100 dark:bg-dark-bg-secondary gap-2 cursor-pointer"
        onClick={() => openFileModal(file)}
      >
        <DocumentText size={64} variant="Bold" className="text-light-button-primary dark:text-blue-400" />
        <span className="text-xs text-gray-500 text-center px-4 truncate w-full">{file.name || 'Document'}</span>
      </div>
    );
  };

  const actionButtons = (
    <div className="flex flex-row items-center gap-2 shrink-0" data-testid={dataTestId ? `${dataTestId}-actions` : undefined}>
      {isEditable && (
        <div
          data-testid={dataTestId ? `${dataTestId}-edit-button` : undefined}
          onClick={() => onEdit?.(itemId)}
          className="text-[#3C91E6] dark:text-blue-400 p-2 rounded-full flex justify-center items-center cursor-pointer hover:bg-blue-50 dark:hover:bg-dark-bg-secondary duration-200 ease-in-out"
        >
          <Edit2 variant="Bold" size={18} />
        </div>
      )}
      {isDelete && (
        <div
          data-testid={dataTestId ? `${dataTestId}-delete-button` : undefined}
          onClick={() => onDelete?.(itemId)}
          className="text-[#CC2936] dark:text-red-400 p-2 rounded-full flex justify-center items-center cursor-pointer hover:bg-red-50 dark:hover:bg-dark-bg-secondary duration-200 ease-in-out"
        >
          <Trash size={18} />
        </div>
      )}
    </div>
  );

  const renderCatalogueMedia = () => {
    if (displayAttachments.length === 0) return null;

    if (displayAttachments.length === 1) {
      return (
        <div className="w-[200px] h-[200px] shrink-0 overflow-hidden rounded-xl bg-gray-50">
          <div className="w-full h-full">
            {renderCarouselSlide(displayAttachments[0])}
          </div>
        </div>
      );
    }

    return (
      <div className="w-[200px] h-[200px] shrink-0 overflow-hidden rounded-xl bg-gray-50">
        <Carousel
          autoplay={displayAttachments.every((file) => getMediaType(file) === 'image')}
          arrows
          infinite
          className="w-full h-full [&_.slick-slide]:h-[200px] [&_.slick-slide>div]:h-full [&_.slick-slider]:h-full [&_.slick-list]:h-full [&_.slick-track]:h-full"
        >
          {displayAttachments.map((file, idx) => (
            <div key={idx} className="w-full h-full">
              {renderCarouselSlide(file)}
            </div>
          ))}
        </Carousel>
      </div>
    );
  };

  if (catalogueMode) {
    return (
      <>
        <div
          data-testid={dataTestId}
          className={cn(
            'flex gap-6 items-center transition-all duration-200',
            {
              'border border-light-input-border dark:border-dark-input-border p-5 rounded-xl bg-white dark:bg-transparent': !noBorder,
            }
          )}
        >
          {renderCatalogueMedia()}
          <div className="flex flex-col gap-4 flex-1 min-w-0">
            <TitleText text={title} size="lg" className="!text-[18px] !font-bold text-[#090909] dark:text-dark-text-primary" />
            {description && (
              <div className="flex flex-col gap-1">
                <span className="text-sm font-semibold text-[#090909] dark:text-dark-text-primary">
                  {t('components.description')}
                </span>
                <InfoText text={description} className="text-[#6B7280] dark:text-dark-text-secondary whitespace-pre-wrap" maxLength={300} />
              </div>
            )}
            {specs && (
              <div className="flex flex-col gap-1">
                <span className="text-sm font-semibold text-[#090909] dark:text-dark-text-primary">
                  {t('components.catalogueSpecs')}
                </span>
                <InfoText text={specs} className="text-[#6B7280] dark:text-dark-text-secondary whitespace-pre-wrap" maxLength={300} />
              </div>
            )}
            {link && (
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-[#3C91E6] hover:underline break-all"
              >
                {link}
              </a>
            )}
          </div>
          {actionButtons}
        </div>

        <CustomModal centered open={!!filePreview} onCancel={() => setFilePreview(null)} title={t('components.preview_file')}>
          {filePreview?.type === 'video' ? (
            <video src={filePreview.url} controls autoPlay className="w-full h-auto max-h-[70vh]" playsInline />
          ) : (
            filePreview && <img src={filePreview.url} alt="Preview" className="w-full h-auto" />
          )}
        </CustomModal>
      </>
    );
  }

  return (
    <>
      <div
        data-testid={dataTestId}
        className={cn(
          listAttachmentsMode ? 'flex flex-col gap-4' : 'grid grid-cols-7 gap-4 items-center transition-all duration-200',
          {
            'border border-light-input-border dark:border-dark-input-border p-4 rounded-xl bg-white dark:bg-transparent': !noBorder,
          }
        )}
      >
        {listAttachmentsMode ? (
          <>
            <div className="flex justify-between items-start gap-4">
              <div className="flex flex-col gap-1 flex-1 min-w-0">
                {date && <InfoText text={date} data-testid={dataTestId ? `${dataTestId}-date` : undefined} />}
                <TitleText text={title} size="lg" className="font-semibold" data-testid={dataTestId ? `${dataTestId}-title` : undefined} />
                {description && <InfoText text={description} className="break-all" data-testid={dataTestId ? `${dataTestId}-description` : undefined} />}
                {link && (
                  <a
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-[#3C91E6] hover:underline break-all"
                    data-testid={dataTestId ? `${dataTestId}-link` : undefined}
                  >
                    {link}
                  </a>
                )}
              </div>
              {actionButtons}
            </div>
            {displayAttachments.length > 0 && (
              <div className="flex flex-col gap-3">
                <div className="flex justify-between text-sm text-[#6B7280]">
                  <span className='!text-[14px] !font-semibold text-[#090909]'>{t('components.attachments')}</span>
                  <span className="text-right ">{displayAttachments.length}/{Math.max(displayAttachments.length, 3)} {t('components.documents')}</span>
                </div>
                {displayAttachments.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 rounded-xl border border-light-input-border dark:border-dark-input-border">
                    <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0 bg-gray-50">
                      {renderMedia(file, 'w-full h-full', true)}
                    </div>
                    <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                      <TitleText text={file.name || 'Document'} size="sm" className="truncate" />
                    </div>
                    <button
                      onClick={() => openFileModal(file)}
                      className="p-2 rounded-full bg-[#EBF3FB] text-[#3C91E6] shrink-0"
                    >
                      <Eye size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            {displayAttachments.length > 0 && (
              <div className="col-span-7 sm:col-span-2 h-40 w-full overflow-hidden rounded-lg bg-gray-50 relative group">
                <Image.PreviewGroup>
                  {displayAttachments.length > 1 ? (
                    <Carousel autoplay={displayAttachments.every((file) => getMediaType(file) === 'image')} arrows infinite className="w-full h-full [&_.slick-slide]:h-40 [&_.slick-slide>div]:h-full [&_.slick-slider]:h-full [&_.slick-list]:h-full [&_.slick-track]:h-full">
                      {displayAttachments.map((file, idx) => (
                        <div key={idx} className="w-full h-full">
                          {renderCarouselSlide(file)}
                        </div>
                      ))}
                    </Carousel>
                  ) : (
                    <div className="w-full h-full">
                      {renderCarouselSlide(displayAttachments[0])}
                    </div>
                  )}
                </Image.PreviewGroup>
              </div>
            )}
            <div className={cn('flex flex-col gap-2', displayAttachments.length > 0 ? 'col-span-7 sm:col-span-4' : 'col-span-5 sm:col-span-5')}>
              {date && <InfoText text={date} />}
              <TitleText text={title} size="lg" className="font-semibold" />
              {description && <InfoText text={description} />}
              {link && (
                <a
                  href={link}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-[#3C91E6] hover:underline break-all"
                  data-testid={dataTestId ? `${dataTestId}-link` : undefined}
                >
                  {link}
                </a>
              )}
            </div>
            <div className={cn('flex flex-row justify-end items-center gap-2', displayAttachments.length > 0 ? 'col-span-7 sm:col-span-1' : 'col-span-2 sm:col-span-2')}>
              {actionButtons}
            </div>
          </>
        )}
      </div>

      <CustomModal centered open={!!filePreview} onCancel={() => setFilePreview(null)} title={t('components.preview_file')}>
        {filePreview?.type === 'video' ? (
          <video src={filePreview.url} controls autoPlay className="w-full h-auto max-h-[70vh]" playsInline />
        ) : (
          filePreview && <img src={filePreview.url} alt="Preview" className="w-full h-auto" />
        )}
      </CustomModal>
    </>
  );
}
